/**
 * Template utility functions for managing message templates
 */
import defaultTemplates from '../config/messageTemplatesConfig';
import { getMessageTemplates, updateMessageTemplates, resetMessageTemplates } from '../services/templatesApi';

/**
 * Initialize templates with defaults if needed
 * @returns {Promise<Object>} The initialized templates
 */
export const initializeTemplates = async () => {
  try {
    // Try to get templates from server
    // console.log('Initializing templates');
    const templates = await getMessageTemplates();
    
    if (templates && Object.keys(templates).length > 0) {
      // console.log('Templates found on server');
      return templates;
    }
    
    // If no templates found, create with defaults
    console.log('No templates found, creating defaults');
    const newTemplates = await updateMessageTemplates(defaultTemplates);
    return newTemplates;
  } catch (error) {
    console.error('Failed to initialize templates:', error);
    // Return defaults if server operations fail
    return defaultTemplates;
  }
};

/**
 * BIGLI-35（PM Bridget 2026-09-04「目前門市資訊還是沒有顯示出來」）：
 * 把伺服器回來的那份 templates **併進**預設值，而不是「驗不過就整包丟掉」。
 *
 * 為什麼一定要用合併：
 *   舊寫法是 validateTemplates(data) 不過就 setTemplates(defaultTemplates)——整包換掉。
 *   預設值裡 storeProfiles 是 []、根本沒有 stores 這個 key（config/messageTemplatesConfig.js:17），
 *   所以只要驗證失敗一次，BIGLI-33／34／35 的門市抬頭、門市 LOGO、門市 Line 客服
 *   就全部連同門市清單一起消失，畫面上的症狀正是「門市資訊完全沒顯示」。
 *   而驗證失敗是**線上的常態**：正式站的 companyEmail 就是空字串（公司資訊區塊已於本票隱藏，
 *   之後也不會有人再去填），舊的 requiredFields 卻把它列為必填。
 *
 * 合併方向：預設值墊底、伺服器值覆蓋。伺服器明確存成空字串（使用者自己清空的欄位）
 * 會照樣蓋掉預設值，只有 undefined / null（schema 沒有這個欄位）才落回預設值——
 * 不然使用者清空 portfolioUrl 之後，預設值會自己長回來。
 *
 * @param {Object} data 伺服器回來的 templates
 * @returns {Object} 一定是物件，且必定含有預設值的每一個 key
 */
export const mergeWithDefaultTemplates = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { ...defaultTemplates };
  }

  const merged = { ...defaultTemplates };
  Object.keys(data).forEach((key) => {
    if (data[key] !== undefined && data[key] !== null) {
      merged[key] = data[key];
    }
  });
  return merged;
};

/**
 * Validates template data structure
 *
 * BIGLI-35：companyName / companyEmail 已從必填名單移除。這兩欄在 MessageTemplates schema
 * 的預設值就是空字串（models/MessageTemplates.js:12-13），而「公司資訊」設定區塊已於本票隱藏
 * ——使用者連填的入口都沒有了，卻讓它們決定整份 templates 的生死，等於把門市資料綁在
 * 一個沒人維護得到的欄位上。留下來的四個欄位是畫面上真的會印出來的字串，schema 有非空預設值。
 *
 * 注意：本函式現在只用來「發警告」，回傳 false 不代表可以把伺服器資料丟掉
 * （見 mergeWithDefaultTemplates 的說明）。
 *
 * @param {Object} templates - The templates object to validate
 * @returns {boolean} Whether the templates are valid
 */
export const validateTemplates = (templates) => {
  if (!templates || typeof templates !== 'object') {
    console.error('Templates invalid: not an object', templates);
    return false;
  }

  // Check for required fields
  const requiredFields = [
    'remarksSectionTitle',
    'nextActionSectionTitle',
    'quotationNextAction',
    'invoicePaymentInstruction'
  ];

  for (const field of requiredFields) {
    if (!templates[field]) {
      console.error(`Templates invalid: missing required field ${field}`);
      return false;
    }
  }
  
  return true;
};

/**
 * Safely get template value with fallback
 * @param {Object} templates - The templates object
 * @param {string} key - The template key to retrieve
 * @param {string} defaultValue - Default value if key not found
 * @returns {string} The template value or default
 */
export const getTemplateValue = (templates, key, defaultValue = '') => {
  if (!templates || !templates[key]) {
    return defaultValue;
  }
  return templates[key];
};
